import React, { useState, useEffect } from "react";
import { Modal, Input, Typography, Divider } from "antd";

const { TextArea } = Input;
const { Text } = Typography;

const cardStyle = {
  marginTop: 12,
  padding: 12,
  backgroundColor: "#fafafa",
  border: "1px solid #f0f0f0",
  borderRadius: 6,
};

/**
 * Reusable confirmation shell for critical "Data Correction & Override" actions.
 *
 * Centralises the shared safety UX so every override modal behaves identically:
 *  - a before → after summary card,
 *  - a MANDATORY reason field (submit stays disabled until it is valid),
 *  - an optional type-to-confirm phrase for irreversible actions,
 *  - a slot for action-specific inputs and warning banners.
 *
 * The parent owns the action-specific state and receives the validated reason
 * via onConfirm(reason).
 */
const CriticalActionModal = ({
  visible,
  onCancel,
  onConfirm,
  loading = false,
  title,
  okText = "Confirmer",
  danger = false,
  summaryItems = [], // [{ label, from?, to }]
  children = null, // action-specific inputs (amount, checkbox, etc.)
  warningBanner = null, // prominent <Alert /> rendered at the top
  confirmPhrase = null, // when set, user must type it to enable submit
  minReasonLength = 10,
  extraValid = true, // parent can block submit (e.g. invalid amount)
}) => {
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (visible) {
      setReason("");
      setConfirmText("");
    }
  }, [visible]);

  const reasonValid = reason.trim().length >= minReasonLength;
  const phraseValid = !confirmPhrase || confirmText.trim() === String(confirmPhrase).trim();
  const canSubmit = reasonValid && phraseValid && extraValid && !loading;

  const handleOk = () => {
    if (canSubmit) onConfirm(reason.trim());
  };

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onCancel}
      onOk={handleOk}
      okText={okText}
      cancelText="Annuler"
      confirmLoading={loading}
      okButtonProps={{ disabled: !canSubmit, danger }}
      width={620}
      destroyOnClose
    >
      {warningBanner}

      {summaryItems.length > 0 && (
        <div style={cardStyle}>
          <Text strong>Résumé de la modification</Text>
          <Divider style={{ margin: "8px 0" }} />
          {summaryItems.map((item, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                marginBottom: 4,
              }}
            >
              <Text type="secondary">{item.label}</Text>
              <span style={{ textAlign: "right" }}>
                {item.from !== undefined && item.from !== null && (
                  <Text delete type="secondary" style={{ marginRight: 8 }}>
                    {item.from}
                  </Text>
                )}
                <Text strong>{item.to}</Text>
              </span>
            </div>
          ))}
        </div>
      )}

      {children}

      <div style={{ marginTop: 16 }}>
        <Text strong>
          Raison <Text type="danger">*</Text>
        </Text>
        <TextArea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={`Expliquez la raison de cette action (min. ${minReasonLength} caractères)…`}
          maxLength={1000}
          showCount
          status={reason.length > 0 && !reasonValid ? "error" : ""}
          style={{ marginTop: 6 }}
        />
        {reason.length > 0 && !reasonValid && (
          <Text type="danger" style={{ fontSize: 12 }}>
            La raison doit contenir au moins {minReasonLength} caractères.
          </Text>
        )}
      </div>

      {confirmPhrase && (
        <div style={{ marginTop: 12 }}>
          <Text strong>
            Pour confirmer, saisissez <Text code>{confirmPhrase}</Text>
          </Text>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={String(confirmPhrase)}
            status={confirmText.length > 0 && !phraseValid ? "error" : ""}
            style={{ marginTop: 6 }}
          />
        </div>
      )}

      {/* Breathing room between the last field and the modal footer */}
      <div style={{ height: 16 }} />
    </Modal>
  );
};

export default CriticalActionModal;
